import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthModalProvider } from "@/context/AuthModalContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import RootRoute from "@/components/RootRoute";
import AuthRoute from "@/components/AuthRoute";
import AuthModal from "@/components/AuthModal";
import ScrollToTop from "@/components/ScrollToTop";
import SiteGate from "@/components/SiteGate";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Checkout from "@/pages/Checkout";
import Orders from "@/pages/Orders";
import Forum from "@/pages/Forum";
import ForumDetail from "@/pages/ForumDetail";
import CreateForumPost from "@/pages/CreateForumPost";
import Social from "@/pages/Social";
import Messages from "@/pages/Messages";
import PriceCompare from "@/pages/PriceCompare";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import PointsLeaderboard from "@/pages/PointsLeaderboard";
import Clubs from "@/pages/Clubs";
import ClubPage from "@/pages/ClubPage";
// import IRacing from "@/pages/IRacing"; // temporarily disabled
import SimHub from "@/pages/SimHub";
import Leaderboards from "@/pages/Leaderboards";
import Admin from "@/pages/Admin";
import Sponsorship from "@/pages/Sponsorship";
import LegalPage from "@/pages/LegalPage";
import SponsorshipTerms from "@/pages/SponsorshipTerms";
import MyListings from "@/pages/MyListings";
import CreateService from "@/pages/CreateService";
import ServiceDetail from "@/pages/ServiceDetail";
import Storefront from "@/pages/Storefront";
import StorefrontEdit from "@/pages/StorefrontEdit";
import AdminServiceCategories from "@/pages/AdminServiceCategories";
import AdminReportedReviews from "@/pages/AdminReportedReviews";
import Watchlist from "@/pages/Watchlist";
import PendingApproval from "@/pages/PendingApproval";
import VerifyEmailFromLink from "@/pages/VerifyEmailFromLink";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicProfile from "@/pages/PublicProfile";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import LearnMarketplace from "@/pages/learn/LearnMarketplace";
import LearnSponsorship from "@/pages/learn/LearnSponsorship";
import LearnPriceCompare from "@/pages/learn/LearnPriceCompare";
import LearnGridPoints from "@/pages/learn/LearnGridPoints";
import LearnHowItWorks from "@/pages/learn/LearnHowItWorks";
import MembershipBenefits from "@/pages/MembershipBenefits";

const protectedRoute = (el) => (
  <ProtectedRoute>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <SiteGate>
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthModalProvider>
          <Routes>
          <Route path="/login" element={<AuthRoute mode="login" />} />
          <Route path="/register" element={<AuthRoute mode="register" />} />
          <Route path="/membership" element={<MembershipBenefits />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/verify-email" element={<VerifyEmailFromLink />} />
          <Route path="/u/:slug" element={<PublicProfile />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* Public "learn more" pages */}
          <Route path="/learn/marketplace" element={<LearnMarketplace />} />
          <Route path="/learn/sponsorship" element={<LearnSponsorship />} />
          <Route path="/learn/price-compare" element={<LearnPriceCompare />} />
          <Route path="/learn/grid-points" element={<LearnGridPoints />} />
          <Route path="/learn/how-it-works" element={<LearnHowItWorks />} />

          <Route path="/" element={<RootRoute />} />
          <Route path="/marketplace" element={protectedRoute(<Marketplace />)} />
          <Route path="/marketplace/create" element={protectedRoute(<CreateListing />)} />
          <Route path="/marketplace/:id/edit" element={protectedRoute(<CreateListing />)} />
          <Route path="/marketplace/:id" element={protectedRoute(<ListingDetail />)} />
          <Route path="/checkout/:id" element={protectedRoute(<Checkout />)} />
          <Route path="/services/create" element={protectedRoute(<CreateService />)} />
          <Route path="/services/:id" element={protectedRoute(<ServiceDetail />)} />
          <Route path="/storefront/edit" element={protectedRoute(<StorefrontEdit />)} />
          <Route path="/storefront/:userId" element={protectedRoute(<Storefront />)} />
          <Route path="/admin/service-categories" element={protectedRoute(<AdminServiceCategories />)} />
          <Route path="/admin/reported-reviews" element={protectedRoute(<AdminReportedReviews />)} />
          <Route path="/forum" element={protectedRoute(<Forum />)} />
          <Route path="/forum/create" element={protectedRoute(<CreateForumPost />)} />
          <Route path="/forum/:id" element={protectedRoute(<ForumDetail />)} />
          <Route path="/social" element={protectedRoute(<Social />)} />
          <Route path="/saved" element={<Navigate to="/social?tab=saved" replace />} />
          <Route path="/messages" element={protectedRoute(<Messages />)} />
          <Route path="/messages/:id" element={protectedRoute(<Messages />)} />
          <Route path="/price-compare" element={protectedRoute(<PriceCompare />)} />
          <Route path="/watchlist" element={protectedRoute(<Watchlist />)} />
          <Route path="/profile" element={protectedRoute(<Profile />)} />
          <Route path="/settings" element={protectedRoute(<Settings />)} />
          <Route path="/points" element={protectedRoute(<PointsLeaderboard />)} />
          <Route path="/clubs" element={protectedRoute(<Clubs />)} />
          <Route path="/clubs/:id" element={protectedRoute(<ClubPage />)} />
          {/* <Route path="/iracing" element={protectedRoute(<IRacing />)} /> disabled — coming later */}
          <Route path="/simhub" element={protectedRoute(<SimHub />)} />
          <Route path="/leaderboards" element={protectedRoute(<Leaderboards />)} />
          <Route path="/admin" element={protectedRoute(<Admin />)} />
          <Route path="/sponsorship" element={protectedRoute(<Sponsorship />)} />
          <Route path="/my-listings" element={protectedRoute(<MyListings />)} />
          <Route path="/orders" element={protectedRoute(<Orders />)} />
          <Route path="/privacy" element={<LegalPage slug="privacy" />} />
          <Route path="/sponsorship-terms" element={<SponsorshipTerms />} />
          <Route path="/terms" element={<LegalPage slug="terms" />} />
        </Routes>
        <AuthModal />
        <Toaster position="top-right" richColors closeButton />
        </AuthModalProvider>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
    </SiteGate>
  );
}

export default App;
